import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import CambiarClaveModal from '../CambiarClaveModal';
import api from '../../utils/api';
import {
  LayoutDashboard, FileText, PenLine, Thermometer, Sparkles, Bug,
  RotateCcw, AlertTriangle, Users, Building2, BookOpen, FolderOpen,
  BarChart3, LogOut, Shield, GitBranch, UserCog, Menu, X, ChevronRight,
  PackageCheck, AlertOctagon
} from 'lucide-react';

const navItems = [
  { section: 'Principal' },
  { path: '/', label: 'Panel Principal', icon: LayoutDashboard },
  { path: '/firmas', label: 'Firmas Pendientes', icon: PenLine, badge: 'firmas' },

  { section: 'Procedimientos' },
  { path: '/pnts', label: 'PNTs y Procedimientos', icon: FileText },

  { section: 'Registros Diarios' },
  { path: '/temperatura', label: 'Control de Temperatura', icon: Thermometer, badge: 'rec_temperatura' },
  { path: '/limpieza', label: 'Limpieza', icon: Sparkles, badge: 'rec_limpieza' },
  { path: '/plagas', label: 'Control de Plagas', icon: Bug, badge: 'rec_plagas' },

  { section: 'Gestión' },
  { path: '/recepciones', label: 'Recepción de Mercancías', icon: PackageCheck },
  { path: '/incidencias', label: 'Hoja de Incidencias', icon: AlertOctagon },
  { path: '/devoluciones', label: 'Devoluciones', icon: RotateCcw },
  { path: '/no-conformidades', label: 'No Conformidades', icon: AlertTriangle },
  { path: '/proveedores', label: 'Proveedores', icon: Building2 },
  { path: '/formacion', label: 'Formación', icon: BookOpen },

  { section: 'Documentación' },
  { path: '/archivos', label: 'Documentos Subidos', icon: FolderOpen },
  { path: '/informes', label: 'Informes', icon: BarChart3 },

  { section: 'Sistema' },
  { path: '/organigrama', label: 'Organigrama', icon: GitBranch },
  { path: '/usuarios', label: 'Usuarios y Permisos', icon: UserCog, adminOnly: true },
  { path: '/auditoria', label: 'Auditoría', icon: Shield, adminOnly: true },
];

const rolLabels = {
  administrador: 'Administrador',
  directora_tecnica: 'Directora Técnica',
  almacen: 'Almacén',
  administracion: 'Administración',
  consulta: 'Consulta'
};

export default function Layout() {
  const { user, logout, isAdmin, debeCambiarClave } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [firmasPendientes, setFirmasPendientes] = useState(0);
  const [recordatorios, setRecordatorios] = useState({}); // { temperatura:true, limpieza:true, plagas:true }
  const [menuAbierto, setMenuAbierto] = useState(false);
  const sidebarRef = useRef();

  const recargarContadores = useCallback(() => {
    api.get('/firmas/pendientes').then(r => setFirmasPendientes(r.data.length)).catch(() => {});
    api.get('/dashboard/recordatorios-mensuales').then(r => {
      // Construir un objeto { temperatura:true, limpieza:true, plagas:true } según pendientes
      const mapa = {};
      (r.data?.pendientes || []).forEach(p => { mapa[p.tipo] = true; });
      setRecordatorios(mapa);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    recargarContadores();
    const interval = setInterval(recargarContadores, 60000);
    return () => clearInterval(interval);
  }, [recargarContadores]);

  // Recargar contadores también cada vez que se cambia de página
  // (así tras firmar y volver al menú, el número está actualizado)
  useEffect(() => {
    recargarContadores();
  }, [location.pathname, recargarContadores]);

  // Cerrar menú al cambiar de página
  useEffect(() => {
    setMenuAbierto(false);
  }, [location.pathname]);

  // Cerrar menú al pulsar fuera
  useEffect(() => {
    const handler = (e) => {
      if (menuAbierto && sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setMenuAbierto(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuAbierto]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = () => {
    if (!user) return '?';
    return `${user.nombre?.[0] || ''}${user.apellidos?.[0] || ''}`.toUpperCase();
  };

  const handleNav = (path) => {
    navigate(path);
    setMenuAbierto(false);
  };

  return (
    <div className="app-layout">

      {/* ── Overlay móvil ── */}
      {menuAbierto && (
        <div
          className="sidebar-overlay"
          onClick={() => setMenuAbierto(false)}
        />
      )}

      {/* ── Sidebar ── */}
      <aside ref={sidebarRef} className={`sidebar ${menuAbierto ? 'sidebar-open' : ''}`}>
        <div className="sidebar-logo">
          <div className="brand">
            <div>
              <div className="brand-name">LOMHIFAR</div>
              <div className="brand-sub">Gestor PNT · v1.0</div>
            </div>
          </div>
          {/* Botón cerrar en móvil */}
          <button
            className="sidebar-close-btn"
            onClick={() => setMenuAbierto(false)}
          >
            <X size={20} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item, i) => {
            if (item.section) {
              return <div key={i} className="sidebar-section">{item.section}</div>;
            }
            if (item.adminOnly && !isAdmin()) return null;

            const isActive = item.path === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(item.path);
            const Icon = item.icon;
            const badgeCount = item.badge === 'firmas' ? firmasPendientes : 0;

            // Badge rojo de recordatorio (punto) para temperatura/limpieza/plagas
            const tieneRecordatorio =
              (item.badge === 'rec_temperatura' && recordatorios.temperatura) ||
              (item.badge === 'rec_limpieza' && recordatorios.limpieza) ||
              (item.badge === 'rec_plagas' && recordatorios.plagas);

            return (
              <button
                key={item.path}
                className={`sidebar-item ${isActive ? 'active' : ''}`}
                onClick={() => handleNav(item.path)}
              >
                <Icon size={16} />
                <span className="sidebar-item-label">{item.label}</span>
                {badgeCount > 0 && <span className="sidebar-badge">{badgeCount}</span>}
                {tieneRecordatorio && (
                  <span
                    title="Informe pendiente"
                    style={{
                      marginLeft: 'auto',
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      background: '#EF4444',
                      flexShrink: 0,
                      boxShadow: '0 0 0 3px rgba(239,68,68,0.25)',
                    }}
                  />
                )}
              </button>
            );
          })}
        </nav>

        <div className="sidebar-user">
          <div className="sidebar-avatar">{getInitials()}</div>
          <div className="sidebar-user-info">
            <div className="name">{user?.nombre} {user?.apellidos}</div>
            <div className="role">{rolLabels[user?.rol] || user?.rol}</div>
          </div>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={handleLogout} title="Cerrar sesión">
            <LogOut size={15} style={{ color: 'rgba(255,255,255,.4)' }} />
          </button>
        </div>
      </aside>

      {/* ── Main content ── */}
      <main className="app-main">

        {/* Barra superior móvil */}
        <div className="mobile-topbar">
          <button
            className="mobile-menu-btn"
            onClick={() => setMenuAbierto(true)}
          >
            <Menu size={22} />
          </button>
          <div className="mobile-brand">LOMHIFAR</div>
          {firmasPendientes > 0 && (
            <button
              className="mobile-firmas-btn"
              onClick={() => handleNav('/firmas')}
            >
              <PenLine size={18} />
              <span>{firmasPendientes}</span>
            </button>
          )}
          <button className="mobile-logout-btn" onClick={handleLogout}>
            <LogOut size={18} />
          </button>
        </div>

        <div className="app-content">
          <Outlet />
        </div>
      </main>

      {debeCambiarClave && <CambiarClaveModal />}
    </div>
  );
}
